<template>
  <div>
    <div class="row align-items-center">
      <div class="col font-weight-bold">Tasks completed</div>
      <div class="col text-right">
        <button
            type="button"
            class="btn btn-link font-weight-bold text-grey-02"
            style="padding-right: 0 !important;"
            @click="triggerAddNewTodoTask"
        >
          <AddPlusIcon/>
          Add
        </button>
      </div>
    </div>

    <div class="row text-center" v-if="!dataLoaded">
      <div class="col">
         <span
             class="spinner-border"
             role="status"
         >
          <span class="sr-only">Loading...</span>
      </span>
      </div>
    </div>

    <div class="row" v-else>
      <div class="col">
        <task-item
            v-for="task in todoTasks"
            :task="task"
            :key="`todo-${task.id}`"
            :isPersonal="true"
            :labelOptions="labelOptions"
            @taskUpdated="updateTaskList"
            @taskDeleted="deleteFromTaskList"
            @saveLabel="saveLabel"
            @deleteLabel="deleteLabel"
        ></task-item>
      </div>
    </div>

    <GoalList
        :yourGoals="yourGoals"
        @storeTasksLocally="storeTasksLocally"
    />

    <div class="mt-3">
      <input
          ref="add-task"
          class="form-control"
          :class="inputClass"
          @focus="addNewTodoTaskFocused = true"
          @keydown.enter="saveNewTask"
          @keydown.esc="cancel"
          placeholder="Add new to do task / goal"
          v-model="addNewTodoTaskTitle"
      />
      <div v-if="!disabledAddTask" class="row mt-2">
        <div class="col-12">
          <div
              class="d-flex justify-content-between align-items-center action-wrapper"
          >
            <button
                class="advanced-button"
                style="background-color: transparent; border: none"
                @click="showAdvance"
            >
              Advanced
            </button>
            <div class="d-flex">
              <button
                  class="border-0 bg-transparent no-outline"
                  @click="updatingLabels = true"
              >
                <img
                    :src="'/images/tag.svg'"
                    style="width: 24px; height: 24px"
                />
              </button>
              <button
                  class="border-0 bg-transparent no-outline"
                  @click="selectingAssignees = true"
              >
                <img :src="'/images/person-add.svg'"/>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <staff-selection
        v-if="selectingAssignees"
        :existingMembers="selectedAssignees"
        @closeModal="selectingAssignees = false"
        @receiveStaffs="updateSelectedAssignees"
    ></staff-selection>

    <add-label-modal
        v-if="updatingLabels"
        :required="false"
        :options="labelOptions"
        :selected="selectedLabel"
        @selectItem="temporarilySaveTaskLabel"
        @deleteItem="deleteLabel"
        @updateLabel="updateLabel"
        @closeModal="updatingLabels = false"
    ></add-label-modal>
  </div>
</template>

<script>
import Task from '../Task';
import { store } from '../../../services/taskService';
import GoalList from './GoalList';
import AddPlusIcon from '../../../icons/AddPlusIcon';

export default {
  name: 'TodoTaskList',

  components: { AddPlusIcon, GoalList },

  props: ['yourTodoTasks', 'yourGoals'],

  data () {
    return {
      todoTasks: this.yourTodoTasks ?? [],
      taskService: Task,
      addNewTodoTaskFocused: false,
      addNewTodoTaskTitle: null,
      selectingAssignees: false,
      selectedAssignees: new Map(),
      temporaryLabel: null,
      selectedLabel: null,
      updatingLabels: false,
      dataLoaded: !!this.yourTodoTasks,
    };
  },

  computed: {
    currentUser () {
      return this.$store.state.currentUser;
    },

    labelOptions () {
      return this.$store.state.labelOptions;
    },

    disabledAddTask () {
      return !(this.addNewTodoTaskFocused || this.addNewTodoTaskTitle);
    },

    inputClass () {
      return { 'bg-light': this.disabledAddTask, 'border-0': this.disabledAddTask };
    },
  },

  methods: {
    storeTasksLocally (eventData) {
      this.$emit('storeTasksLocally', eventData);
    },

    async fetchTodoTasks () {
      const filters = {
        assigned_to: 'personal',
        type: 'todo',
        range: 'today',
        status: 'today_with_pickup',
        per_page: 200,
      };

      const res = await this.taskService.fetchTasks(filters);
      let tasks = res.data;
      let tasksWithSubtasks = [];

      tasks.forEach((item) => {
        if (item.total_subtasks > 0) {
          tasksWithSubtasks.push(item.id);
        }
      });

      if (tasksWithSubtasks.length > 0) {
        const res = await axios.get('/task/subtasks', {
          params: {
            ids: tasksWithSubtasks.join(','),
          },
        });
        const subtasks = res.data.data;

        tasks = tasks.map((item) => {
          const subtask = subtasks[item.id];

          if (!!subtask) {
            item['subtasks'] = subtask;
          }

          return item;
        });
      }

      this.todoTasks = tasks;
      this.dataLoaded = true;
      this.storeTasksLocally({
        key: 'yourTodoTasks',
        tasks: tasks,
      });
    },

    async saveNewTask () {
      const assignees = this.selectedAssignees.size === 0 ? [this.currentUser.id] :
          [...this.selectedAssignees].map(value => {
            return value[0];
          });

      const response = await store({ type: 'todo', title: this.addNewTodoTaskTitle }, {
        assignees,
        label_id: this.temporaryLabel,
      });

      this.resetFields();

      if (assignees.includes(this.currentUser.id)) {
        this.todoTasks.push(response.data.data);
      }
    },

    updateTaskList (updatedTask) {
      let taskExists = false;

      this.todoTasks = this.todoTasks.map(task => {
        if (task.id === updatedTask.id) {
          task = updatedTask;
          taskExists = true;
        }

        return task;
      });

      if (!taskExists) {
        this.todoTasks.push(updatedTask);
      }

      this.storeTasksLocally({
        key: 'yourTodoTasks',
        tasks: this.todoTasks,
      });
    },

    deleteFromTaskList (deletedTask) {
      this.todoTasks = this.todoTasks.filter(task => {
        return task.id !== deletedTask.id;
      });

      this.storeTasksLocally({
        key: 'yourTodoTasks',
        tasks: this.todoTasks,
      });
    },

    temporarilySaveTaskLabel (value, labelId) {
      this.selectedLabel = value;
      this.temporaryLabel = labelId;
    },

    async saveLabel (data) {
      const { label } = data;
      let found = false;

      this.$store.state.labelOptions = this.labelOptions.map(value => {
        if (value.id === label.id) {
          found = true;
          return label;
        }

        return value;
      });

      if (!found) {
        this.$store.state.labelOptions.push({
          ...label
        });
      }
    },

    async updateLabel (val, labelId) {
      let label = null;

      if (labelId) {
        const res = await axios.patch(`/company/${this.currentUser.company.id}/labels/${labelId}`, { label: val });
        label = res.data.data;
      } else {
        const res = await axios.post(`/company/${this.currentUser.company.id}/labels`, { label: val });
        label = res.data.data;
      }

      await this.saveLabel({ val, label });
    },

    async deleteLabel (labelId) {
      await axios.delete(`/company/${this.currentUser.company.id}/labels/${labelId}`);
      const newLabels = this.labelOptions.filter(value => {
        return value.id !== labelId;
      });

      this.$store.state.labelOptions = newLabels.map(value => {
        return value;
      });
    },

    resetFields () {
      this.addNewTodoTaskTitle = null;
      this.selectedLabel = null;
      this.temporaryLabel = null;
      this.selectedAssignees = new Map();
    },

    cancel () {
      this.addNewTodoTaskFocused = false;
      this.resetFields();
      this.$nextTick(() => {
        this.$refs['add-task'].blur();
      });
    },

    showAdvance () {
      const params = {
        title: this.addNewTodoTaskTitle ?? '',
        type: 'todo',
      };

      const queryString = Object.keys(params).map(key => {
        return `${key}=${encodeURIComponent(params[key])}`;
      }).join('&');

      window.location.href = `/tasks/advanced?${queryString}`;
    },

    updateSelectedAssignees (newStaffs) {
      this.selectedAssignees = newStaffs;
    },

    triggerAddNewTodoTask () {
      this.$refs['add-task'].focus();
      this.$refs['add-task'].scrollIntoView({ block: 'center', behavior: 'smooth' });
    },

    registerTaskUpdatedListener () {
      window.Echo.private(`tasks-updated.${this.currentUser.company.id}`).listen(
          '.tasks-updated',
          e => {
            this.dataLoaded = false;
            this.fetchTodoTasks();
          },
      )
    },
  },

  mounted () {
    if (!this.yourTodoTasks) {
      this.fetchTodoTasks();
    }

    this.registerTaskUpdatedListener();
  },
};
</script>

<style scoped>

</style>
